import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    DomainListResponse,
    UnsubscribeResponse,
} from '../model/unsubscribe.interface';
import { ToastrService } from 'ngx-toastr';
import {
    DownloadOptOutsParam,
    UnsubscribeDownloadSSE,
    UnsubscribeType,
} from '../model/campaign.interface';
import * as moment from 'moment';

@Injectable({
    providedIn: 'root',
})
export class UnsubscribeService {
    marketingAutomationCampaignApi: string =
        'api/v1/platform/marketing-automation-campaign/unsubscribe-management';
    domainListApi: string =
        '/api/v1/platform/marketing-automation-campaign/unsubscribe-management/domains';
    unSubscribeManagementSMSDownloadApi: string =
        '/api/v1/platform/marketing-automation-campaign/unsubscribe-management/report';
    activePhoneNumber:string = '/api/v1/platform/marketing-automation-campaign/unsubscribe-management/active-phone-number'
    private activeTab = new BehaviorSubject<number>(0);
    private activeTab$ = this.activeTab.asObservable();
    private eventSource: EventSource | null = null;

    constructor(
        private http: HttpClient,
        private zone: NgZone,
        private toastr: ToastrService,
    ) {}

    getUnsubscribers(
        page: number,
        sortBy: string,
        sortOrder: string,
        limit: number,
        search: string,
        type: string,
        domainName: string,
        startDate?: Date,
        endDate?: Date,
    ): Observable<UnsubscribeResponse> {
        const obj = {
            limit: limit,
            pageNumber: page,
            sortBy: sortBy,
            sortOrder: sortOrder,
            search: search ? search : '',
        };
        if (startDate && endDate) {
            const startDateStr =moment.utc(startDate).local().format(('MM/DD/YYYY'));
            const endDateStr = moment.utc(endDate).local().format(('MM/DD/YYYY'));
            obj['startDate'] = startDateStr;
            obj['endDate'] = endDateStr;
            obj['timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
        if (type === 'email') {
            obj['domain'] = domainName ? domainName : '';
        }
        let Api = '';
        if (type && type.toLowerCase() === 'sms') {
            Api = `${this.marketingAutomationCampaignApi}/sms/`;
        } else {
            Api = `${this.marketingAutomationCampaignApi}/email/`;
        }

        return this.http.get<UnsubscribeResponse>(Api, { params: obj });
    }

    reSubscribe(
        type: string,
        id:string
    ): Observable< { isActionSuccess: boolean; message: string }> {
        let apiUrl = '';
        if (type && type.toLowerCase() === 'sms') {
            apiUrl  = `${this.marketingAutomationCampaignApi}/sms/`;
        } else {
            apiUrl = `${this.marketingAutomationCampaignApi}/email/`;
        }
        return this.http.delete< { isActionSuccess: boolean; message: string }>(`${apiUrl}${id}`);
    }

    getDomainList(): Observable<DomainListResponse> {
        return this.http.get<DomainListResponse>(this.domainListApi);
    }
    getActiveTab(): Observable<number> {
        return this.activeTab$;
    }
    setActiveTab(tabIndex: number) {
        this.activeTab.next(tabIndex);
    }

    startUnsubscribeDownload(param: DownloadOptOutsParam): Observable<void> {
        return new Observable<void>((observer) => {
        if (this.eventSource) {
            observer.error(new Error('Export is already in progress.'));
        }

        window.addEventListener('beforeunload', this.leavingPage);
        const params: any = {
          search: param.search,
        };

        if( param.startDate && param.endDate){
            const startDateStr =moment.utc(param.startDate).local().format(('MM/DD/YYYY'));
            const endDateStr = moment.utc(param.endDate).local().format(('MM/DD/YYYY'));
            params.startDate = startDateStr;
            params.endDate = endDateStr;
        }

        if (param.type === UnsubscribeType.EMAIL) {
            params.domain = param.domainName;
        }
        params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const query = new URLSearchParams(params).toString();
        const url = `${this.unSubscribeManagementSMSDownloadApi}/${param.type}?${query}`;
        this.eventSource = new EventSource(url, { withCredentials: true });


        this.eventSource.addEventListener('started', () => {
            this.zone.run(() => {
                this.toastr.success('Preparing your file for download…');
            });
        });

        this.eventSource.addEventListener('completed', (event) => {
            const data: UnsubscribeDownloadSSE = JSON.parse(event.data);
            this.zone.run(() => {
                try {
                    this.downloadUnSubscriberCSVByUrl(data.url,data.fileName);
                    this.closeUnSubscribesFileDownloadSSE();
                    observer.next();
                    observer.complete();
                } catch (error) {
                    this.closeUnSubscribesFileDownloadSSE();
                    observer.error(new Error('Failed to process export file download.'));
                }
            });
        });

        this.eventSource.addEventListener('failed', () => {
             this.zone.run(() => {
                this.closeUnSubscribesFileDownloadSSE();
                this.toastr.error(
                    'Failed to download the file. Please try again later.',
                );
                observer.error(new Error('Failed to download the file. Please try again later.'));
            });
        });

        this.eventSource.onerror = () => {
            this.zone.run(() => {
                if (this.eventSource && (this.eventSource.readyState === EventSource.CLOSED || this.eventSource.readyState === EventSource.CONNECTING)) {
                    this.eventSource.close();
                    this.eventSource = null;
                    window.removeEventListener('beforeunload', this.leavingPage);
                    observer.error(new Error('Unable to complete export. Please try again.'));
                }
            });
        };

        return () => {
            this.closeUnSubscribesFileDownloadSSE();
        };
      });
    }

    closeUnSubscribesFileDownloadSSE() {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
        window.removeEventListener('beforeunload', this.leavingPage);
    }
    downloadUnSubscriberCSVByUrl(fileUrl: string, fileName: string): void {
        const a = document.createElement('a');
        a.href = fileUrl;
        a.target = '_blank';
        a.click();
    }

    leavingPage(event): any {
        event.preventDefault();
        event.returnValue = '';
        return '';
    }
    addOptOuts(payload: { phoneNumbers?: string[]; emails?: string[]; domain?: string; type: string }): Observable<any> {
        const apiUrl = `${this.marketingAutomationCampaignApi}/${payload.type}/`;
        let body: any = {};

        if (payload.type === 'sms') {
            body = {
                phoneNumbers: payload.phoneNumbers,
            };
        } else if (payload.type === 'email') {
            body = {
                emails: payload.emails,
                domain: payload.domain,
            };
        }
        return this.http.post<any>(apiUrl, body);
    }
    getActivePhone():Observable<{ senderPhoneNumber: string }> {
        return this.http.get<{ senderPhoneNumber: string }>(`${this.activePhoneNumber}`);
    }
}
